// A short two-note tone for new critical alerts, made with WebAudio (no sound file). Browsers only let a page
// play sound after the person interacted with it (a click, a tap released, a key), so the audio context is
// created and resumed then; until it runs, `alertToneReady()` is false and the switch says so.

import { ALERT_TONE } from '@constants/timing';

type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
let lastPlayed = 0;
let armed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Subscribe to the audio becoming usable (or not); for useSyncExternalStore. */
export function onAlertToneState(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Whether a tone can play right now (audio unlocked by an interaction). */
export const alertToneReady = () => ctx?.state === 'running';

function create(): AudioContext | null {
  if (ctx) return ctx;
  const C: AudioCtor | undefined = window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext;
  if (!C) return null;
  try {
    ctx = new C();
    ctx.addEventListener('statechange', notify);
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Try to start audio (call it from an interaction). Resolves true once it runs. */
export async function unlockAlertTone(): Promise<boolean> {
  const c = create();
  if (!c) return false;
  if (c.state !== 'running') await c.resume().catch(() => undefined);
  notify();
  return c.state === 'running';
}

const GESTURES = ['pointerup', 'click', 'keydown'] as const;

/**
 * Unlock audio at the next interaction with the page (the switch was on from an earlier visit, or the browser
 * suspended it again). The listeners stay until audio really runs: a touch that did not count keeps them.
 */
export function armAlertTone() {
  if (armed || alertToneReady()) return;
  armed = true;
  const go = () => {
    void unlockAlertTone().then((ok) => {
      if (!ok) return;
      armed = false;
      for (const g of GESTURES) window.removeEventListener(g, go, true);
    });
  };
  for (const g of GESTURES) window.addEventListener(g, go, true);
}

/**
 * Two short notes, falling. At most one per ALERT_TONE.minGapMs (a burst of alerts is one tone) unless `force`
 * (the preview when sound is switched on). Returns whether it played; when audio was suspended again (some
 * browsers do after a while) the next interaction unlocks it.
 */
export function playAlertTone(force = false): boolean {
  const now = Date.now();
  if (!force && now - lastPlayed < ALERT_TONE.minGapMs) return false;
  const c = ctx;
  if (!c || c.state !== 'running') {
    armAlertTone();
    return false;
  }
  lastPlayed = now;
  const t0 = c.currentTime + ALERT_TONE.leadSec;
  ALERT_TONE.notesHz.forEach((hz, i) => {
    const start = t0 + (i * ALERT_TONE.noteMs) / 1000;
    const end = start + ALERT_TONE.noteMs / 1000;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = hz;
    gain.gain.setValueAtTime(ALERT_TONE.silentGain, start);
    gain.gain.exponentialRampToValueAtTime(ALERT_TONE.volume, start + ALERT_TONE.attackSec);
    gain.gain.exponentialRampToValueAtTime(ALERT_TONE.silentGain, end);
    osc.connect(gain).connect(c.destination);
    osc.start(start);
    osc.stop(end + ALERT_TONE.leadSec);
  });
  return true;
}
